import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaSearch, FaFileDownload, FaSpinner, FaCalendar, FaClock,
  FaEye, FaUser, FaMoneyBillWave, FaIdCard, FaCheckCircle,
  FaTimesCircle, FaArrowLeft, FaArrowRight, FaFilter, FaSync,
  FaExclamationTriangle, FaInfoCircle, FaTag, FaBuilding
} from 'react-icons/fa';
import { baseUrl } from '../../config';

const RefundHistory = () => {
  const [refunds, setRefunds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showTodayOnly, setShowTodayOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // For testing purposes, you can use this mock data
  /*
  useEffect(() => {
    // Mock data that matches the structure shown in your response
    const mockData = [
      {
        adId: {
          _id: '68c91de95c78947082e0ec5a',
          adType: 'Banner',
          totalCost: '1472.6399999999999',
          status: 'rejected'
        },
        advertiserName: "Deepa",
        createdAt: "2025-09-16T08:21:24.827Z",
        reason: "not a valid add",
        refundAmount: 1472.64,
        refundId: "REFUND-MFMAAG9D1E6LYT",
        updatedAt: "2025-09-16T08:21:24.827Z",
        __v: 0,
        _id: "68c91e045c78947082e0ec6d"
      }
    ];
    
    setRefunds(mockData);
    setLoading(false);
  }, []);
  */

  // Fetch refund history on component mount
  useEffect(() => {
    const fetchRefundHistory = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/refund/history`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Refund history response:", response);
        // The API response structure is: response.data.data (array)
        setRefunds(Array.isArray(response.data.data) ? response.data.data : []);
        setError(null);
      } catch (err) {
        console.error("Error fetching refund history:", err);
        setError("Failed to load refund history. Please try again later.");
        setRefunds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRefundHistory();
  }, []);

  // Function to filter data between start and end dates
  const filterDataByDateRange = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      alert("Start date cannot be after end date");
      return;
    }
  };

  // Reset date filters
  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
    setShowTodayOnly(false);
  };

  // Handle Today checkbox
  const handleTodayCheckbox = (e) => {
    const isChecked = e.target.checked;
    setShowTodayOnly(isChecked);

    if (isChecked) {
      // Set today's date for both start and end using local timezone
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`; // Format: YYYY-MM-DD
      setStartDate(formattedDate);
      setEndDate(formattedDate);
    } else {
      // Clear the dates if unchecked
      setStartDate("");
      setEndDate("");
    }
  };

  // Filter refunds by ID, user name, amount, or date
  const filteredRefunds = refunds.filter((refund) => {
    // Add null checks for refund object
    if (!refund) return false;

    const refundId = refund.refundId?.toLowerCase() || "";
    const advertiserName = refund.advertiserName?.toLowerCase() || "";
    const email = refund.advertiserId?.email?.toLowerCase() || "";
    const mobile = refund.advertiserId?.mobile?.toLowerCase() || "";
    const adId = refund.adId?._id?.toLowerCase() || "";
    const adType = refund.adId?.adType?.toLowerCase() || "";
    const amount = refund.refundAmount?.toString() || "";
    const reason = refund.reason?.toLowerCase() || "";
    const adStatus = refund.adId?.status?.toLowerCase() || "";
    const date = refund.createdAt
      ? new Date(refund.createdAt).toLocaleDateString().toLowerCase()
      : "";
    const search = searchTerm.toLowerCase();

    // Apply text search filter
    const matchesSearch = !searchTerm ||
      refundId.includes(search) ||
      advertiserName.includes(search) ||
      email.includes(search) ||
      mobile.includes(search) ||
      adId.includes(search) ||
      adType.includes(search) ||
      amount.includes(search) ||
      reason.includes(search) ||
      adStatus.includes(search) ||
      date.includes(search);

    // Apply date range filter
    const refundDate = refund.createdAt ? new Date(refund.createdAt) : null;

    // Create new date objects to avoid mutating the original dates
    const filterStartDate = startDate ? new Date(startDate + 'T00:00:00') : null;
    const filterEndDate = endDate ? new Date(endDate + 'T23:59:59.999') : null;

    const withinDateRange =
      (!filterStartDate || (refundDate && refundDate >= filterStartDate)) &&
      (!filterEndDate || (refundDate && refundDate <= filterEndDate));

    return matchesSearch && withinDateRange;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRefunds.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRefunds.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    if (filteredRefunds.length === 0) {
      alert("No data to export");
      return;
    }

    // Create CSV header row
    const headers = [
      "Refund ID",
      "Advertiser",
      "Ad ID",
      "Ad Type",
      "Ad Status",
      "Amount",
      "Reason",
      "Created Date"
    ].join(",");

    // Create CSV data rows
    const csvData = filteredRefunds.map((refund) => {
      return [
        refund.refundId || "",
        refund.advertiserName || "",
        refund.adId?._id || "",
        refund.adId?.adType || "",
        refund.adId?.status || "",
        refund.refundAmount || "0",
        refund.reason || "",
        refund.createdAt ? formatDate(refund.createdAt) : ""
      ].join(",");
    });

    // Combine header and data
    const csv = [headers, ...csvData].join("\n");

    // Create a Blob and download link
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", `refund_history_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 p-4 sm:p-6">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-red-500 to-pink-500 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                <FaMoneyBillWave className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Refund History</h1>
                <p className="text-red-100">Track all refund transactions and their status</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">
                {filteredRefunds.length}
              </div>
              <div className="text-red-100">Total Refunds</div>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="p-6 bg-gray-50">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by Refund ID, Advertiser, Ad ID, Amount..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Date Range Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2">
                <FaCalendar className="text-red-500 w-4 h-4" />
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setShowTodayOnly(false);
                  }}
                  max={endDate || undefined}
                  disabled={showTodayOnly}
                />
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-gray-400">to</span>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setShowTodayOnly(false);
                  }}
                  min={startDate || undefined}
                  disabled={showTodayOnly}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="today-checkbox"
                  type="checkbox"
                  className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                  checked={showTodayOnly}
                  onChange={handleTodayCheckbox}
                />
                <label htmlFor="today-checkbox" className="text-sm font-medium text-gray-700">
                  Today Only
                </label>
              </div>

              <button
                onClick={filterDataByDateRange}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
                disabled={!startDate || !endDate}
              >
                <FaSearch className="w-4 h-4" />
                <span>Filter</span>
              </button>

              {(startDate || endDate) && (
                <button
                  onClick={clearDateFilters}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all duration-200"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Filter Status */}
          {startDate && endDate && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <FaInfoCircle className="text-red-500 w-4 h-4" />
                <span className="text-red-700 font-medium">
                  {showTodayOnly
                    ? "Showing today's refunds only"
                    : `Showing refunds between ${new Date(startDate).toLocaleDateString()} and ${new Date(endDate).toLocaleDateString()}`
                  }
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-red-200 border-t-red-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <FaSpinner className="w-6 h-6 text-red-500 animate-pulse" />
              </div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading refund history...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <FaExclamationTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center space-x-2"
            >
              <FaSync className="w-4 h-4" />
              <span>Try Again</span>
            </button>
          </div>
        ) : filteredRefunds.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FaMoneyBillWave className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Refunds Found</h3>
            <p className="text-gray-600 text-center">
              {searchTerm || startDate || endDate
                ? "No refunds match your current filters. Try adjusting your search criteria."
                : "There are no refund records to display at this time."
              }
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-red-500 to-pink-500">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaCalendar className="w-4 h-4" />
                        Date
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaIdCard className="w-4 h-4" />
                        Refund ID
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaUser className="w-4 h-4" />
                        Advertiser
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaTag className="w-4 h-4" />
                        Ad Type
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaMoneyBillWave className="w-4 h-4" />
                        Amount
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaInfoCircle className="w-4 h-4" />
                        Reason
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <FaCheckCircle className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((refund, index) => (
                    <tr key={refund.refundId || index} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <FaClock className="w-4 h-4 text-gray-400" />
                          <span>{formatDate(refund.createdAt)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {refund.refundId?.charAt(0) || 'R'}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 font-mono">
                              {refund.refundId || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {refund.advertiserName || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {refund.adId?.adType || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-lg font-bold text-red-600">
                          ₹{refund.refundAmount?.toLocaleString() || '0'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 max-w-xs truncate" title={refund.reason || 'N/A'}>
                          {refund.reason || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${refund.adId?.status === 'approved' ? 'bg-green-100 text-green-800' :
                            refund.adId?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              refund.adId?.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                          }`}>
                          {refund.adId?.status || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden p-4 space-y-4">
              {currentItems.map((refund, index) => (
                <div key={refund.refundId || index} className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-4 border border-red-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                        {refund.refundId?.charAt(0) || 'R'}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{refund.advertiserName || 'N/A'}</div>
                        <div className="text-sm text-gray-600 font-mono">{refund.refundId || 'N/A'}</div>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 bg-blue-100 text-blue-800">
                          {refund.adId?.adType || 'N/A'}
                        </span>
                      </div>
                    </div>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${refund.adId?.status === 'approved' ? 'bg-green-100 text-green-800' :
                        refund.adId?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          refund.adId?.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                      }`}>
                      {refund.adId?.status || 'Unknown'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-600">Amount</div>
                      <div className="text-lg font-bold text-red-600">₹{refund.refundAmount?.toLocaleString() || '0'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Date</div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatDate(refund.createdAt)}
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600 mb-2">Reason</div>
                  <div className="text-sm text-gray-900 bg-white p-2 rounded-lg border">
                    {refund.reason || 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && !error && filteredRefunds.length > 0 && totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(indexOfLastItem, filteredRefunds.length)}
                </span>{" "}
                of <span className="font-medium">{filteredRefunds.length}</span> results
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-1 ${currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-red-50 hover:border-red-300 border-gray-300"
                    }`}
                >
                  <FaArrowLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNumber = i + 1;
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${currentPage === pageNumber
                            ? "bg-red-500 text-white"
                            : "bg-white text-gray-700 hover:bg-red-50 border border-gray-300"
                          }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 rounded-lg border transition-all duration-200 flex items-center space-x-1 ${currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-red-50 hover:border-red-300 border-gray-300"
                    }`}
                >
                  <span>Next</span>
                  <FaArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RefundHistory;
